import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { useApi, useAuth } from '../../Hooks'

// How often every signed-in frontend reports presence and picks up the
// maintenance state. This doubles as the "push" latency for maintenance mode.
const HEARTBEAT_INTERVAL_MS = 45000

export interface MaintenanceStatus {
  enabled: boolean
  message?: string | null
  updatedBy?: string | null
  updatedAtUtc?: string | null
}

interface SystemStatusValue {
  maintenance: MaintenanceStatus
  // Stages with a scheduled/active rollout — manual tenant updates are blocked.
  reservedEnvironments: string[]
  // Subset of the above: stages whose rollout is scheduled but not yet running.
  scheduledEnvironments: string[]
  refreshSystemStatus: () => void
}

const defaultMaintenance: MaintenanceStatus = { enabled: false }

const SystemStatusContext = createContext<SystemStatusValue>({
  maintenance: defaultMaintenance,
  reservedEnvironments: [],
  scheduledEnvironments: [],
  refreshSystemStatus: () => {}
})

export const SystemStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isLoggedIn } = useAuth()
  const { fetchApi } = useApi()
  const [maintenance, setMaintenance] = useState<MaintenanceStatus>(defaultMaintenance)
  const [reservedEnvironments, setReservedEnvironments] = useState<string[]>([])
  const [scheduledEnvironments, setScheduledEnvironments] = useState<string[]>([])
  const beatRef = useRef<() => void>(() => {})

  const heartbeat = useCallback(async () => {
    if (!isLoggedIn) return
    try {
      const response = await fetchApi({ endpoint: '/api/presence/heartbeat', method: 'PUT', body: {} })
      if (response?.maintenance) {
        setMaintenance(response.maintenance)
      }
      setReservedEnvironments(response?.reservedEnvironments ?? [])
      setScheduledEnvironments(response?.scheduledEnvironments ?? [])
    } catch (error) {
      // Presence is best-effort; never bother the user about a failed beat.
      console.debug('Heartbeat failed', error)
    }
  }, [isLoggedIn, fetchApi])
  beatRef.current = heartbeat

  useEffect(() => {
    if (!isLoggedIn) return
    beatRef.current()
    const interval = setInterval(() => beatRef.current(), HEARTBEAT_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isLoggedIn])

  return (
    <SystemStatusContext.Provider value={{ maintenance, reservedEnvironments, scheduledEnvironments, refreshSystemStatus: heartbeat }}>
      {children}
    </SystemStatusContext.Provider>
  )
}

export const useSystemStatus = () => useContext(SystemStatusContext)
